/**
 * VoiceModeModal (Web) — ChatGPT-style hands-free voice loop
 *
 * STT  : Web SpeechRecognition API  (instant, no upload latency)
 * LLM  : Gemini
 * TTS  : Bytez OpenAI tts-1 (nova voice) → AudioContext → play
 */

import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { callGeminiAI } from "../lib/gemini";

const BYTEZ_KEY  = process.env.REACT_APP_BYTEZ_API_KEY;
const TTS_URL    = "https://api.bytez.com/models/v2/openai/tts-1";

const SYSTEM_PROMPT =
  "You are SupaDoc, a friendly AI health assistant. " +
  "STRICT RULES: " +
  "1. Reply ONLY in Hindi or English — never any other language. " +
  "2. If user speaks Hindi or Hinglish, reply in Hindi. If English, reply in English. " +
  "3. Keep reply to 2-3 sentences only — it will be spoken aloud. " +
  "4. NO markdown, NO asterisks, NO bullets — plain speech only. " +
  "5. For serious conditions always say: consult a real doctor.";

async function callLLM(history) {
  const { output, error } = await callGeminiAI(history.slice(-10), SYSTEM_PROMPT);
  if (error) throw new Error(error);
  return output?.trim() || "";
}

async function callTTS(text, audioCtxRef) {
  const r = await fetch(TTS_URL, {
    method: "POST",
    headers: {
      lang: "javascript",
      Authorization: `Key ${BYTEZ_KEY}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      input: text.replace(/[*#_`>]/g, "").replace(/\n+/g, " ").slice(0, 600),
      voice: "nova",
    }),
  });
  if (!r.ok) throw new Error(`TTS ${r.status}`);
  const ab = await r.arrayBuffer();

  // Lazily create AudioContext with user-gesture unlock
  if (!audioCtxRef.current || audioCtxRef.current.state === "closed") {
    audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
  }
  const ctx = audioCtxRef.current;
  if (ctx.state === "suspended") await ctx.resume();

  const decoded = await ctx.decodeAudioData(ab);
  return new Promise((resolve) => {
    const src = ctx.createBufferSource();
    src.buffer = decoded;
    src.connect(ctx.destination);
    src.onended = resolve;
    src.start(0);
    // store ref so we can stop it
    audioCtxRef._currentSrc = src;
  });
}

// ─── Phase colours ────────────────────────────────────────────────
const PHASE_LABEL = {
  idle:         "Tap to start",
  listening:    "Listening…",
  thinking:     "Thinking…",
  speaking:     "Speaking…",
};

const ORB_BG = {
  idle:         "from-slate-600 to-slate-800",
  listening:    "from-blue-600 to-violet-700",
  thinking:     "from-violet-600 to-purple-700",
  speaking:     "from-emerald-500 to-teal-600",
};

const RING_COLOR = {
  idle:         "border-slate-500",
  listening:    "border-blue-500",
  thinking:     "border-violet-500",
  speaking:     "border-emerald-500",
};

// ─── Orb ──────────────────────────────────────────────────────────
function Orb({ phase }) {
  const rings = phase === "listening" ? [0, 0.33, 0.66] : [];

  return (
    <div className="relative w-48 h-48 flex items-center justify-center select-none">
      {/* Expanding rings – listening only */}
      {rings.map((delay, i) => (
        <motion.div
          key={i}
          className={`absolute w-32 h-32 rounded-full border-2 ${RING_COLOR[phase]}`}
          style={{ opacity: 0 }}
          animate={{ scale: [1, 2.2], opacity: [0.5, 0] }}
          transition={{ duration: 1.4, delay, repeat: Infinity, ease: "easeOut" }}
        />
      ))}

      {/* Glow */}
      <motion.div
        className={`absolute w-36 h-36 rounded-full bg-gradient-to-br ${ORB_BG[phase]} opacity-20 blur-xl`}
        animate={phase === "speaking" ? { scale: [1, 1.3, 1] } : { scale: 1 }}
        transition={{ duration: 0.7, repeat: phase === "speaking" ? Infinity : 0 }}
      />

      {/* Core orb */}
      <motion.div
        className={`w-28 h-28 rounded-full bg-gradient-to-br ${ORB_BG[phase]} shadow-2xl flex items-center justify-center cursor-pointer`}
        animate={
          phase === "thinking"
            ? { rotate: 360 }
            : phase === "speaking"
            ? { scale: [1, 1.1, 0.95, 1.08, 1] }
            : phase === "idle"
            ? { scale: [1, 1.04, 1] }
            : {}
        }
        transition={
          phase === "thinking"
            ? { duration: 1.6, repeat: Infinity, ease: "linear" }
            : phase === "speaking"
            ? { duration: 0.7, repeat: Infinity }
            : { duration: 2, repeat: Infinity }
        }
      >
        {/* Inner dot */}
        <div className="w-10 h-10 rounded-full bg-white/20" />
      </motion.div>
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────
export default function VoiceModeModal({ open, onClose, onExchange }) {
  const [phase, setPhase]       = useState("idle");
  const [userText, setUserText] = useState("");
  const [aiText, setAiText]     = useState("");
  const [hint, setHint]         = useState("");

  const phaseRef    = useRef("idle");
  const activeRef   = useRef(false);
  const processing  = useRef(false);
  const history     = useRef([]);
  const recognRef   = useRef(null);
  const audioCtxRef = useRef(null);

  const setP = useCallback((p) => { phaseRef.current = p; setPhase(p); }, []);

  // ── Stop TTS playback ─────────────────────────────────────────────
  const stopSpeaking = useCallback(() => {
    try { audioCtxRef._currentSrc?.stop(); } catch (_) {}
  }, []);

  // ── Process transcript → LLM → TTS ───────────────────────────────
  const processTranscript = useCallback(async (transcript) => {
    if (!activeRef.current || processing.current) return;
    processing.current = true;

    // Non-Hindi/English filter (Whisper-style hallucination guard)
    const bad = /[\u3000-\u9FFF\uAC00-\uD7AF\u0600-\u06FF\u4E00-\u9FFF]/;
    if (bad.test(transcript) || transcript.trim().length < 2) {
      processing.current = false;
      if (activeRef.current) startListening();
      return;
    }

    setUserText(transcript);
    history.current.push({ role: "user", content: transcript });

    setP("thinking");
    setHint("");

    try {
      const reply = await callLLM(history.current);
      history.current.push({ role: "assistant", content: reply });
      setAiText(reply);
      if (onExchange) onExchange(transcript, reply);

      if (!activeRef.current) { processing.current = false; return; }
      setP("speaking");
      setHint("Tap orb to skip");

      await callTTS(reply, audioCtxRef);

    } catch (err) {
      console.error("[VoiceMode]", err);
      setHint(`Error: ${err.message}. Retrying…`);
      await new Promise(r => setTimeout(r, 1500));
    }

    processing.current = false;
    if (activeRef.current) startListening();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExchange, setP]);

  // ── Start listening via Web SpeechRecognition ─────────────────────
  const startListening = useCallback(() => {
    if (!activeRef.current) return;

    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setHint("SpeechRecognition not supported in this browser. Use Chrome.");
      return;
    }

    const recog = new SR();
    recognRef.current = recog;
    recog.continuous    = false;
    recog.interimResults = false;
    recog.lang          = "hi-IN"; // Works for both Hindi and English

    recog.onstart = () => {
      setP("listening");
      setUserText(""); setAiText("");
      setHint("Speak now… (auto-detects when you stop)");
    };

    recog.onresult = (e) => {
      const t = Array.from(e.results)
        .map(r => r[0].transcript)
        .join(" ")
        .trim();
      if (t) processTranscript(t);
    };

    recog.onerror = (e) => {
      if (e.error === "no-speech") {
        // Restart automatically
        if (activeRef.current && phaseRef.current === "listening") setTimeout(startListening, 300);
      } else {
        setHint(`Mic error: ${e.error}`);
      }
    };

    recog.onend = () => {
      // If still listening phase but no result came, restart
      if (activeRef.current && phaseRef.current === "listening" && !processing.current) {
        setTimeout(startListening, 200);
      }
    };

    try { recog.start(); } catch (_) {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processTranscript, setP]);

  // ── Open / Close ───────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      activeRef.current   = true;
      processing.current  = false;
      history.current     = [];
      setP("idle");
      setUserText(""); setAiText(""); setHint("Starting…");
      // slight delay for modal animation
      setTimeout(startListening, 400);
    } else {
      activeRef.current = false;
      processing.current = false;
      recognRef.current?.abort();
      stopSpeaking();
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {});
        audioCtxRef.current = null;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Orb tap ────────────────────────────────────────────────────────
  const onOrbTap = () => {
    if (phaseRef.current === "speaking") { stopSpeaking(); return; }
    if (phaseRef.current === "idle")     { startListening(); return; }
    if (phaseRef.current === "listening") {
      recognRef.current?.stop(); // force finalise
    }
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end justify-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          className="w-full max-w-lg bg-slate-900 rounded-t-3xl px-6 pb-12 pt-4 flex flex-col items-center gap-4"
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ type: "spring", damping: 24, stiffness: 140 }}
        >
          {/* Handle */}
          <div className="w-10 h-1 bg-slate-600 rounded-full mb-1" />

          {/* Close */}
          <div className="w-full flex justify-end">
            <button
              onClick={onClose}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800 border border-slate-700 text-slate-400 text-sm hover:bg-slate-700 transition-colors"
            >
              <X size={14} /> End Voice Mode
            </button>
          </div>

          {/* Phase label */}
          <p className="text-white text-2xl font-bold tracking-tight">{PHASE_LABEL[phase]}</p>

          {/* Orb */}
          <div onClick={onOrbTap}>
            <Orb phase={phase} />
          </div>

          {/* Hint */}
          <p className="text-slate-500 text-sm text-center min-h-[40px] px-4">{hint}</p>

          {/* Transcript bubbles */}
          <div className="w-full space-y-2">
            {userText && (
              <div className="flex justify-end">
                <div className="bg-blue-600 text-white text-sm px-4 py-2.5 rounded-2xl rounded-br-sm max-w-[80%]">
                  {userText}
                </div>
              </div>
            )}
            {aiText && (
              <div className="flex justify-start">
                <div className="bg-slate-800 border border-slate-700 text-slate-100 text-sm px-4 py-2.5 rounded-2xl rounded-bl-sm max-w-[86%] line-clamp-4">
                  {aiText}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
